(function() {
  'use strict';

  const whenVisible = window.theme?.whenVisible || ((element, callback) => callback(element));

  function parseAutoplayDelayMs(value, fallbackSeconds) {
    const parsedValue = parseFloat(value);
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
      return fallbackSeconds * 1000;
    }

    const normalizedSeconds = parsedValue > 10 ? parsedValue / 1000 : parsedValue;
    return normalizedSeconds * 1000;
  }

  function initSkincareBlogs(container = document) {
    const sections = container.matches?.('.skincare-blogs')
      ? [container]
      : container.querySelectorAll('.skincare-blogs');

    sections.forEach(function(section) {
      const swiperElement = section.querySelector('.swiper');
      if (!swiperElement || section.swiper) return;

      if (typeof window.loadThemeSwiper !== 'function') {
        console.warn('Swiper loader is unavailable for SkincareBlogs.');
        return;
      }

      window.loadThemeSwiper().then(() => {
        initSwiper(section, swiperElement);
      }).catch((error) => {
        console.warn('Swiper failed to load for SkincareBlogs:', error);
      });
    });
  }

  function initSwiper(section, swiperElement) {
    const originalSlideCount = section.originalSlideCount || swiperElement.querySelectorAll('.swiper-slide').length;
    section.originalSlideCount = originalSlideCount;

    if (section.swiper) {
      section.swiper.destroy(true, true);
      section.swiper = null;
    }

    const navPrev = section.querySelector('.skincare-blogs__nav-btn--prev');
    const navNext = section.querySelector('.skincare-blogs__nav-btn--next');
    const paginationEl = section.querySelector('.swiper-pagination');

    const slidesDesktop = parseInt(section.dataset.slidesDesktop, 10) || 3;
    const slidesTablet = parseInt(section.dataset.slidesTablet, 10) || 2;
    const slidesMobile = parseInt(section.dataset.slidesMobile, 10) || 1;
    const spaceBetween = parseInt(section.dataset.spaceBetween, 10) || 24;
    const spaceBetweenMobile = parseInt(section.dataset.spaceBetweenMobile, 10) || 16;
    const loopEnabled = section.dataset.loop !== 'false';
    const loop = loopEnabled && originalSlideCount > Math.max(slidesDesktop, slidesTablet, slidesMobile);
    const speed = parseInt(section.dataset.speed, 10) || 300;
    const showPagination = section.dataset.showPagination === 'true';
    const autoplayEnabled = section.dataset.autoplay === 'true';
    const autoplay = autoplayEnabled
      ? {
          delay: parseAutoplayDelayMs(section.dataset.autoplayDelay, 4),
          disableOnInteraction: false,
          pauseOnMouseEnter: section.dataset.autoplayPauseOnHover !== 'false'
        }
      : false;

    section.swiper = new Swiper(swiperElement, {
      slidesPerView: slidesDesktop,
      spaceBetween: spaceBetween,
      loop: loop,
      speed: speed,
      watchOverflow: true,
      navigation: navPrev && navNext ? {
        nextEl: navNext,
        prevEl: navPrev
      } : false,
      pagination: showPagination && paginationEl ? {
        el: paginationEl,
        clickable: true
      } : false,
      breakpoints: {
        0: {
          slidesPerView: slidesMobile,
          spaceBetween: spaceBetweenMobile
        },
        640: {
          slidesPerView: slidesTablet,
          spaceBetween: spaceBetween
        },
        1024: {
          slidesPerView: slidesDesktop,
          spaceBetween: spaceBetween
        }
      },
      on: {
        init() {
          syncNavigationAvailability(section);
          syncNavPosition(section);
        },
        resize() {
          syncNavigationAvailability(section);
          syncNavPosition(section);
        },
        breakpoint() {
          syncNavigationAvailability(section);
          syncNavPosition(section);
        },
        lock() {
          syncNavigationAvailability(section);
        },
        unlock() {
          syncNavigationAvailability(section);
        }
      },
      autoplay: autoplay
    });

    syncNavigationAvailability(section);
    bindNavPositionUpdates(section);
    syncNavPosition(section);
  }

  function syncNavigationAvailability(section) {
    const swiper = section.swiper;
    const navPrev = section.querySelector('.skincare-blogs__nav-btn--prev');
    const navNext = section.querySelector('.skincare-blogs__nav-btn--next');

    if (!swiper || !navPrev || !navNext) {
      section.classList.remove('skincare-blogs--can-navigate');
      return;
    }

    const currentSlidesPerView = getCurrentSlidesPerView(swiper);
    const originalSlideCount = section.originalSlideCount || 0;
    const hasOverflow = originalSlideCount > currentSlidesPerView;

    section.classList.toggle('skincare-blogs--can-navigate', hasOverflow);
  }

  function getCurrentSlidesPerView(swiper) {
    const slidesPerView = swiper?.params?.slidesPerView;
    const numericSlidesPerView = typeof slidesPerView === 'number'
      ? slidesPerView
      : parseFloat(slidesPerView);

    return Number.isFinite(numericSlidesPerView) && numericSlidesPerView > 0
      ? numericSlidesPerView
      : 1;
  }

  function syncNavPosition(section) {
    const stage = section.querySelector('.skincare-blogs__stage');
    const imageWrappers = Array.from(section.querySelectorAll('.skincare-blog-card__image-wrapper'));

    if (!stage || !imageWrappers.length) return;

    const stageTop = stage.getBoundingClientRect().top;
    const centers = imageWrappers
      .map((wrapper) => {
        const rect = wrapper.getBoundingClientRect();
        if (!rect.height) return 0;
        return (rect.top - stageTop) + (rect.height / 2);
      })
      .filter(Boolean);

    if (!centers.length) return;

    stage.style.setProperty('--skincare-blogs-nav-center', `${Math.max(...centers)}px`);
  }

  function bindNavPositionUpdates(section) {
    if (section.navPositionResizeHandler) return;

    section.navPositionResizeHandler = () => syncNavPosition(section);
    window.addEventListener('resize', section.navPositionResizeHandler);
  }

  if (!customElements.get('skincare-blogs-section')) {
    class SkincareBlogsSection extends HTMLElement {
      connectedCallback() {
        this.visibilityObserver = whenVisible(this, () => initSkincareBlogs(this));
      }

      disconnectedCallback() {
        this.visibilityObserver?.disconnect?.();

        const section = this.matches('.skincare-blogs') ? this : this.querySelector('.skincare-blogs');
        if (section?.swiper) {
          section.swiper.destroy(true, true);
          section.swiper = null;
        }

        if (section?.navPositionResizeHandler) {
          window.removeEventListener('resize', section.navPositionResizeHandler);
          delete section.navPositionResizeHandler;
        }
      }
    }

    customElements.define('skincare-blogs-section', SkincareBlogsSection);
  }
})();
